import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { brandLogoMap } from "../../assets/dummyData";
import { useHorizontalDragScroll } from "../../utils/useHorizontalDragScroll";
import axios from "axios";
import config from "../../config/config";

const NewOnSaleSection = ({ excludeBrand, hideTabs, defaultTab, title }) => {
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const scrollRefNew = useHorizontalDragScroll();
  const scrollRefSale = useHorizontalDragScroll();

  const [activeTab, setActiveTab] = useState(defaultTab || "new_arrivals");
  const [brandsWithSales, setBrandsWithSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrandsWithSales = async () => {
      try {
        const response = await axios.get(`${config.backendUrl}/inventory/brands_with_sales`);
        setBrandsWithSales(response.data?.results?.brands || []);
      } catch (error) {
        console.error("Error fetching brands with sales:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrandsWithSales();
  }, []);

  const newArrivalBrands = brandLogoMap.filter(brand => brand.name !== excludeBrand);
  const onSaleBrands = brandLogoMap.filter(brand => brandsWithSales.includes(brand.name) && brand.name !== excludeBrand);

  if (loading) {
    return (
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: smallScreen ? 2 : 3, py: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Skeleton variant="rectangular" width={240} height={45} sx={{ borderRadius: "100px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" width={150} height={60} sx={{ borderRadius: "8px" }} />
          ))}
        </Box>
      </Box>
    );
  }

  const renderBrandCard = (brand, type) => (
    <Box
      key={brand.name}
      onClick={() => {
        navigate(`/new-on-sale-products`, {
          state: {
            brandName: brand.name,
            type: type,
            title: `${brand.name} - ${type === "on_sale" ? "On Sale" : "New Arrivals"}`
          },
        });
      }}
      sx={{
        minWidth: "150px",
        height: "60px",
        flexShrink: 0,
        position: "relative",
        cursor: "pointer",
        "&:hover .inner-card": {
          boxShadow: type === "new_arrivals"
            ? "0 12px 30px rgba(125, 211, 224, 0.25)"
            : "0 12px 30px rgba(255, 155, 128, 0.25)",
          transform: "translateY(-4px)",
          borderColor: type === "new_arrivals"
            ? "rgba(125, 211, 224, 0.4)"
            : "rgba(255, 155, 128, 0.4)",
        },
        "&:active .inner-card": {
          transform: "scale(0.96)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        },
      }}
    >
      <Box
        className="inner-card"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "8px",
          border: "1px solid rgba(255,255,255,0.8)",
          background: "linear-gradient(145deg, #ffffff 0%, #f9f9fb 100%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
          overflow: "hidden",
          padding: 1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
        }}
      >
        {brand.logo ? (
          <img
            src={brand.logo}
            alt={brand.name}
            style={{
              width: "100%",
              height: "100%",
              maxWidth: "135px",
              maxHeight: "45px",
              objectFit: "contain",
              transform: brand.scale ? `scale(${brand.scale})` : "none",
            }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: "Poppins-Bold",
              fontSize: "14px",
              color: "#222",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}
          >
            {brand.name}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        px: smallScreen ? 2 : 3,
        pt: smallScreen ? 2 : 2.5,
        pb: 4,
      }}
    >
      {/* Premium Pill Tabs with Sliding Indicator or Custom Title */}
      {!hideTabs ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: smallScreen ? 3.5 : 5,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              position: "relative",
              backgroundColor: "rgba(247, 248, 250, 0.8)",
              backdropFilter: "blur(12px)",
              p: 0.5,
              borderRadius: "100px",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "inset 0 2px 6px rgba(0,0,0,0.02), 0 4px 15px rgba(0,0,0,0.03)",
            }}
          >
            {/* Sliding Background Pill */}
            <Box
              sx={{
                position: "absolute",
                top: "4px",
                bottom: "4px",
                left: "4px",
                width: "calc(50% - 4px)",
                backgroundColor: "#fff",
                borderRadius: "100px",
                boxShadow: activeTab === "new_arrivals"
                  ? "0 4px 16px rgba(125, 211, 224, 0.25)"
                  : "0 4px 16px rgba(255, 155, 128, 0.25)",
                transform: activeTab === "new_arrivals" ? "translateX(0)" : "translateX(100%)",
                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                zIndex: 1,
              }}
            />

            <Box
              onClick={() => setActiveTab("new_arrivals")}
              sx={{
                px: { xs: 3, sm: 4 },
                py: 1,
                cursor: "pointer",
                position: "relative",
                zIndex: 2,
                flex: 1,
                textAlign: "center",
                minWidth: { xs: "130px", sm: "160px" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: activeTab === "new_arrivals" ? "Poppins-Bold" : "Poppins-SemiBold",
                  fontSize: { xs: "13px", sm: "14px" },
                  color: activeTab === "new_arrivals" ? "#7DD3E0" : "#999",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  transition: "color 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                New Arrivals
              </Typography>
            </Box>
            <Box
              onClick={() => setActiveTab("on_sale")}
              sx={{
                px: { xs: 3, sm: 4 },
                py: 1,
                cursor: "pointer",
                position: "relative",
                zIndex: 2,
                flex: 1,
                textAlign: "center",
                minWidth: { xs: "130px", sm: "160px" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: activeTab === "on_sale" ? "Poppins-Bold" : "Poppins-SemiBold",
                  fontSize: { xs: "13px", sm: "14px" },
                  color: activeTab === "on_sale" ? "#FF9B80" : "#999",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  transition: "color 0.3s ease",
                  whiteSpace: "nowrap",
                }}
              >
                On Sale
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Typography 
          sx={{ 
            fontFamily: "Poppins-SemiBold", 
            fontSize: { xs: "20px", sm: "24px" },
            textAlign: "center", 
            mb: 4,
            color: "#333",
            textTransform: "uppercase",
            letterSpacing: "1px"
          }}
        >
          {title || "Explore Other Brands"}
        </Typography>
      )}

      {/* Brand Tiles Carousels (Rendered conditionally to preserve individual scroll states) */}
      <Box sx={{ position: "relative", width: "100%" }}>
        
        {/* Left Arrow Indicator */}
        <Box 
          sx={{
            position: "absolute",
            left: { xs: -12, sm: -24 },
            top: "50%",
            transform: "translateY(-50%)",
            color: activeTab === "new_arrivals" ? "rgba(125, 211, 224, 0.6)" : "rgba(255, 155, 128, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1,
            opacity: 0.8,
            transition: "color 0.4s ease",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </Box>

        {/* Right Arrow Indicator */}
        <Box 
          sx={{
            position: "absolute",
            right: { xs: -12, sm: -24 },
            top: "50%",
            transform: "translateY(-50%)",
            color: activeTab === "new_arrivals" ? "rgba(125, 211, 224, 0.6)" : "rgba(255, 155, 128, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1,
            opacity: 0.8,
            transition: "color 0.4s ease",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </Box>

        <Box sx={{
          position: "relative",
        borderRadius: "16px",
        border: "1px solid",
        borderColor: activeTab === "new_arrivals"
          ? "rgba(125, 211, 224, 0.4)"
          : "rgba(255, 155, 128, 0.4)",
        boxShadow: activeTab === "new_arrivals"
          ? "0 0 24px rgba(125, 211, 224, 0.12), inset 0 0 12px rgba(125, 211, 224, 0.04)"
          : "0 0 24px rgba(255, 155, 128, 0.12), inset 0 0 12px rgba(255, 155, 128, 0.04)",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        backdropFilter: "blur(8px)",
        transition: "all 0.4s ease",
        overflow: "hidden",
      }}>
        <Box sx={{
          WebkitMaskImage: "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
          maskImage: "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
          width: "100%",
        }}>

        {/* New Arrivals Carousel */}
        <Box
          ref={scrollRefNew}
          className="flex overflow-x-auto hide-scrollbar"
          sx={{
            display: activeTab === "new_arrivals" ? "flex" : "none",
            gap: 2,
            px: 2,
            pt: 1, // Space for translateY(-4px)
            pb: 1, // Space for 12px hover shadow
            width: "100%",
            "&::-webkit-scrollbar": { display: "none" },
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
          }}
        >
          {newArrivalBrands.map((brand) => renderBrandCard(brand, "new_arrivals"))}
        </Box>

        {/* On Sale Carousel */}
        <Box
          ref={scrollRefSale}
          className="flex overflow-x-auto hide-scrollbar"
          sx={{
            display: activeTab === "on_sale" ? "flex" : "none",
            gap: 2,
            px: 2,
            pt: 1,
            pb: 1,
            width: "100%",
            "&::-webkit-scrollbar": { display: "none" },
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
          }}
        >
          {onSaleBrands.length === 0 ? (
            <Typography sx={{ fontFamily: "Poppins-Regular", color: "#666", width: "100%", textAlign: "center", py: 2 }}>
              No brands are currently on sale.
            </Typography>
          ) : (
            onSaleBrands.map((brand) => renderBrandCard(brand, "on_sale"))
          )}
        </Box>
        </Box>

      </Box>
      </Box>
    </Box>
  );
};

export default NewOnSaleSection;
